import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '@tanstack/react-form';
import { DialogContent } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select';
import { Field, FieldLabel, FieldContent, FieldError } from '@/components/ui/field';
import { Form } from '@components/Form';
import { useFormContext } from '@components/Form/FormProvider';
import { PRODUCT_FORM_VALUES, DEFAULT_FORM_LABELS } from '@components/Product/types';
import { categoriesForForm, type ProductCategoryOptions } from '@components/Product/categories';
import { useCreateProduct } from '@hooks/products';
import { useTranslation } from '@hooks/useTranslation';
import { toast } from '@/hooks/use-toast';
import { ToastLinkDescription } from '@/components/ui/toast';
import { getFieldErrors } from '@api/client';
import { createProductFormSchema, type CreateProductFormData } from '@/react/schemas/product.schema';
import { cn } from '@/lib/utils';

import { FIELD_SURFACE } from "@/components/ui/field-surface";
type SubmitAction = 'draft' | 'continue';

const INPUT_CLASSES = cn(FIELD_SURFACE, 'h-auto px-6 py-4 font-inter text-sm leading-normal placeholder:text-grey-light placeholder:font-normal');

interface CreateProductFormProps {
  onClose: () => void;
  /** Server-defined category vocabulary, scoped by format. */
  categoryOptions: ProductCategoryOptions;
}

function useServerErrors(error: Error | null) {
  const { form } = useFormContext();
  const { t } = useTranslation();

  useEffect(() => {
    if (!error) return;
    const errors = getFieldErrors(error);
    if (errors?.name) {
      const formValue = form.getFieldValue('form') as string;
      const formatLabel = formValue ? DEFAULT_FORM_LABELS[formValue as keyof typeof DEFAULT_FORM_LABELS] : '';
      const message = formatLabel
        ? t('create_modal.name_duplicate', { format: formatLabel })
        : String(errors.name);
      form.setFieldMeta('name', (prev) => ({
        ...prev,
        errorMap: { ...prev.errorMap, onSubmit: message },
      }));
    }
  }, [error, form, t]);
}

function FormFields({ error, categoryOptions }: { error: Error | null; categoryOptions: ProductCategoryOptions }) {
  const { form } = useFormContext();
  const { t } = useTranslation();
  useServerErrors(error);
  // Tracked at this level rather than inside a form.Field render prop — hooks
  // must not be conditional (same reasoning as CreatePresetMenuForm).
  // `category` is format-scoped on the backend, so the options depend on it.
  const currentForm = useStore(form.store, (state) => (state.values as {form?: string}).form ?? '');
  const availableCategories = categoriesForForm(categoryOptions, currentForm);

  return (
    <div className="flex flex-col gap-6">
      <form.Field name="name">
        {(field) => {
          const fieldError = field.state.meta.errorMap.onSubmit || null;
          return (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.name')}
              </FieldLabel>
              <FieldContent>
                <Input
                  value={field.state.value as string}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_modal.name_placeholder')}
                  className={cn(INPUT_CLASSES, fieldError && 'border-chilli-red')}
                />
                {fieldError && <FieldError>{fieldError}</FieldError>}
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <div className="grid grid-cols-2 gap-6">
        <form.Field name="form">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.format')}
              </FieldLabel>
              <FieldContent>
                <Select
                  value={(field.state.value as string) ?? ''}
                  onValueChange={(newForm) => {
                    field.handleChange(newForm);
                    // Drop a category that does not belong to the new format.
                    const currentCategory = form.getFieldValue('category') as string;
                    if (currentCategory && !categoriesForForm(categoryOptions, newForm).includes(currentCategory)) {
                      form.setFieldValue('category', '');
                    }
                  }}
                >
                  <SelectTrigger>
                    <span className={cn(field.state.value ? 'text-off-black' : 'text-grey-light')}>
                      {field.state.value ? DEFAULT_FORM_LABELS[field.state.value as keyof typeof DEFAULT_FORM_LABELS] : t('create_modal.format_placeholder')}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_FORM_VALUES.map((f) => (
                      <SelectItem key={f} value={f}>{DEFAULT_FORM_LABELS[f]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="category">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.category')}
              </FieldLabel>
              <FieldContent>
                <Select
                  value={(field.state.value as string) ?? ''}
                  onValueChange={field.handleChange}
                  disabled={!currentForm}
                >
                  <SelectTrigger>
                    <span className={cn(field.state.value ? 'text-off-black' : 'text-grey-light')}>
                      {(field.state.value as string) ||
                        (currentForm
                          ? t('create_modal.category_placeholder')
                          : t('create_modal.category_needs_format'))}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {availableCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>
    </div>
  );
}

function FormFooter({ actionRef, isPending }: { actionRef: React.RefObject<SubmitAction>; isPending: boolean }) {
  const { form } = useFormContext();
  const { t } = useTranslation();

  return (
    <form.Subscribe selector={(state) => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting, values: state.values })}>
      {({ canSubmit, isSubmitting, values }) => {
        const typedValues = values as CreateProductFormData;
        const isDisabled = !canSubmit || isSubmitting || isPending || !typedValues?.name || !typedValues?.form || !typedValues?.category;

        return (
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              type="submit"
              variant="outline-subtle"
              disabled={isDisabled}
              onClick={() => { actionRef.current = 'draft'; }}
            >
              {t('create_modal.save_draft')}
            </Button>
            <Button
              type="submit"
              disabled={isDisabled}
              onClick={() => { actionRef.current = 'continue'; }}
            >
              {t('create_modal.continue')}
            </Button>
          </div>
        );
      }}
    </form.Subscribe>
  );
}

export function CreateProductForm({ onClose, categoryOptions }: CreateProductFormProps) {
  const actionRef = useRef<SubmitAction>('continue');
  const { t } = useTranslation();
  const createProduct = useCreateProduct();

  const handleSubmit = useCallback(async (values: CreateProductFormData) => {
    let product;
    try {
      product = (await createProduct.mutateAsync(values)).product;
    } catch {
      // Error surfaced via createProduct.error → useServerErrors
      return;
    }
    if (actionRef.current === 'continue') {
      window.location.href = `/products/${product.id}/edit`;
      return;
    }
    onClose();
    if (actionRef.current === 'draft') {
      const { dismiss } = toast({
        description: (
          <ToastLinkDescription
            name={product.name}
            href={`/products/${product.id}/edit`}
            label={t('create_modal.draft_saved')}
            onNavigate={() => dismiss()}
          />
        ),
      });
    }
  }, [createProduct, t]);

  const title = t('create_modal.title');

  return (
    <DialogContent
      onOpenAutoFocus={(e) => e.preventDefault()}
      onEscapeKeyDown={onClose}
      onInteractOutside={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-radix-popper-content-wrapper]')) {
          e.preventDefault();
        }
      }}
      className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none [&>button:last-child]:hidden"
    >
      <VisuallyHidden.Root>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{title}</DialogDescription>
      </VisuallyHidden.Root>
      <Form
        schema={createProductFormSchema}
        defaultValues={{ name: '', form: '', category: '' }}
        onSubmit={handleSubmit}
        isPending={createProduct.isPending}
        className="flex flex-1 flex-col min-h-0"
      >
        <ModalLayout
          as="div"
          title={<ModalTitle>{title}</ModalTitle>}
          onClose={onClose}
          footer={<FormFooter actionRef={actionRef} isPending={createProduct.isPending} />}
        >
          <FormFields error={createProduct.error} categoryOptions={categoryOptions} />
        </ModalLayout>
      </Form>
    </DialogContent>
  );
}
